import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import en from '../i18n/en';
import en_ca from '../i18n/en_ca';
import fr from '../i18n/fr';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const translations = { en, en_ca, fr };
const languages = [
  { code: 'en', label: 'English (US)', flag: '🇺🇸' },
  { code: 'en_ca', label: 'English (CA)', flag: '🇨🇦' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

const QUEBEC_NAMES = ['quebec', 'québec', 'qc'];
const CANADA_PROVINCES = ['alberta','british columbia','manitoba','new brunswick','newfoundland and labrador','nova scotia','ontario','prince edward island','saskatchewan','northwest territories','nunavut','yukon'];

function detectLanguage(profile) {
  if (!profile) return null;
  const prov = (profile.province_state || '').toLowerCase();
  const country = (profile.country || '').toLowerCase();
  if (QUEBEC_NAMES.includes(prov)) return 'fr';
  if (country === 'canada' || country === 'ca' || CANADA_PROVINCES.includes(prov)) return 'en_ca';
  if (country === 'united states' || country === 'us' || country === 'usa') return 'en';
  return null;
}

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const [autoDetected, setAutoDetected] = useState(false);

  // Auto-detect language from user profile on first login (only if user hasn't manually chosen)
  useEffect(() => {
    const manuallySet = localStorage.getItem('lang_manual');
    if (manuallySet) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const userId = (() => { try { return JSON.parse(atob(token.split('.')[1])).user_id; } catch { return null; } })();
    if (!userId || autoDetected) return;
    axios.get(`${API}/profile/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const detected = detectLanguage(r.data?.profile);
        if (detected && detected !== lang) {
          setLang(detected);
          localStorage.setItem('lang', detected);
        }
        setAutoDetected(true);
      }).catch(() => {});
  }, []);

  const setLangManual = (code) => {
    setLang(code);
    localStorage.setItem('lang', code);
    localStorage.setItem('lang_manual', 'true');
  };

  useEffect(() => {
    document.documentElement.lang = lang.startsWith('en') ? 'en' : lang;
  }, [lang]);

  const t = (key) => translations[lang]?.[key] || translations.en?.[key] || key;

  return (
    <LanguageContext.Provider value={{ lang, setLang: setLangManual, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
